import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { useLocalization } from '@experiences/locales';
import {
    SpacingToken,
    UiSelect,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import {
    formatDate,
    getDateFromNow,
} from '@experiences/util';
import { makeStyles } from '@mui/styles';
import { FontVariantToken } from '@uipath/apollo-core';
import { PortalAlertBar } from '@uipath/portal-shell-react';
import React, { useMemo } from 'react';
import {
    Controller,
    useFormContext,
} from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
    MigrationEndWindowDays,
    MigrationStartWindowDays,
} from '../../../common/interfaces/gws';
import type { ScheduleMode } from '../../../util/MigrationUtil';
import {
    generateLocalizedTimeWindows,
    generateTimeWindows,
    getBrowserTimezone,
} from '../../../util/MigrationUtil';
import { MigrationModeSelector } from './MigrationModeSelector';
import { MigrationScheduleCalendar } from './MigrationScheduleCalendar';
import { MigrationScheduleSummary } from './MigrationScheduleSummary';
import {
    MigrationTimezoneAlert,
    MigrationWhatsNextBox,
} from './migrationShared';
import { MigrationTimeWindowSelect } from './MigrationTimeWindowSelect';
import type { IMigrationFormData } from './types';

const useStyles = makeStyles(theme => ({
    timeSelect: { width: '320px' },
    box: {
        outline: 'solid',
        outlineWidth: '1px',
        outlineColor: theme.palette.semantic.colorBorderDeEmp,
        borderRadius: '3px',
        padding: '16px 32px',
    },
}));

interface MigrationStepSelectTimeProps {
    i18n: {
        downtimeWindow: string;
        dateWindow: string;
        whatsNext: string;
        timeInfoKeys: readonly string[];
    };
}

// ts-unused-exports:disable-next-line
export const MigrationStepSelectTime: React.FC<MigrationStepSelectTimeProps> = ({ i18n }) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const language = useLocalization();
    const {
        control,
        watch,
        setValue,
        formState: { errors },
    } = useFormContext<IMigrationFormData>();

    const enableCustomSchedule = useFeatureFlagValue(Features.EnableCustomMigrationSchedule.name) as boolean;

    const hourKeys = useMemo(generateTimeWindows, []);
    const tz = useMemo(getBrowserTimezone, []);
    const localizedWindows = useMemo(
        () => generateLocalizedTimeWindows(tz.offsetMinutes, tz.short, translate),
        [ tz, translate ],
    );

    const windowStartDate = getDateFromNow(MigrationStartWindowDays);
    const windowEndDate = getDateFromNow(MigrationEndWindowDays);

    const hour = watch('customHour') || watch('hour');
    const scheduleMode = watch('scheduleMode') || 'earliest';
    const customDates = watch('customDates') || [];

    const selectedTw = useMemo(
        () => localizedWindows.find(w => w.utcValue === hour) ?? localizedWindows[0],
        [ localizedWindows, hour ],
    );

    const calendarMinDate = useMemo(() => getDateFromNow(0, true), []);
    const calendarMaxDate = useMemo(() => getDateFromNow(60, true), []);

    const handleModeChange = (mode: ScheduleMode) => {
        setValue('scheduleMode', mode);
    };

    // ── Feature flag OFF: original UI ──
    if (!enableCustomSchedule) {
        return (
            <UiStack
                direction='column'
                gap={SpacingToken.L}>
                <UiStack gap={80}>
                    <UiStack
                        direction='column'
                        gap={SpacingToken.Micro}>
                        <UiText variant={FontVariantToken.fontSizeLBold}>
                            {translate({ id: i18n.downtimeWindow })}
                        </UiText>
                        <UiText>
                            {`${hour.split('-')[0] + ' UTC'} - ${hour.split('-')[1] + ' UTC'}`}
                        </UiText>
                    </UiStack>
                    <UiStack
                        direction='column'
                        gap={SpacingToken.Micro}>
                        <UiText variant={FontVariantToken.fontSizeLBold}>
                            {translate({ id: i18n.dateWindow })}
                        </UiText>
                        <UiText>
                            {formatDate(windowStartDate.toISOString(), language)}
                            {' - '}
                            {formatDate(windowEndDate.toISOString(), language)}
                        </UiText>
                    </UiStack>
                </UiStack>
                <UiStack
                    direction='column'
                    gap={SpacingToken.Micro}>
                    <UiSelect
                        id="customHour"
                        name="customHour"
                        control={control}
                        isTranslated
                        inputLabel={translate({ id: 'CLIENT_CHOOSE_ANOTHER_WINDOW' })}
                        options={hourKeys}
                        error={!!errors.customHour}
                        fullWidth
                        className={classes.timeSelect}
                        dataCy="hour-select-field"
                    />
                    <UiText variant={FontVariantToken.fontSizeS}>
                        {translate({ id: 'CLIENT_RECOMMEND_TIME' })}
                    </UiText>
                </UiStack>

                <MigrationWhatsNextBox
                    whatsNextKey={i18n.whatsNext}
                    timeInfoKeys={i18n.timeInfoKeys}
                    className={classes.box}
                />
            </UiStack>
        );
    }

    // ── Feature flag ON: new UI with mode toggle + timezone ──
    return (
        <UiStack
            direction="column"
            gap={SpacingToken.L}>

            {/* Mode selector */}
            <UiStack
                direction="column"
                gap={SpacingToken.XS}>
                <UiText variant={FontVariantToken.fontSizeLBold}>
                    {translate({ id: 'CLIENT_MIGRATION_SCHEDULE_MODE_LABEL' })}
                </UiText>
                <MigrationModeSelector
                    scheduleMode={scheduleMode}
                    onModeChange={handleModeChange}
                />
            </UiStack>

            {/* ===== EARLIEST AVAILABLE MODE ===== */}
            {scheduleMode === 'earliest' && (
                <>
                    {/* Timezone info */}
                    <MigrationTimezoneAlert tz={tz} />

                    <UiStack gap={80}>
                        <UiStack
                            direction="column"
                            gap={SpacingToken.Micro}>
                            <UiText variant={FontVariantToken.fontSizeLBold}>
                                {translate({ id: i18n.downtimeWindow })}
                            </UiText>
                            <UiText>
                                {selectedTw.utcLabel}
                            </UiText>
                            <UiText variant={FontVariantToken.fontSizeS}>
                                {selectedTw.localLabel}
                            </UiText>
                        </UiStack>
                        <UiStack
                            direction="column"
                            gap={SpacingToken.Micro}>
                            <UiText variant={FontVariantToken.fontSizeLBold}>
                                {translate({ id: i18n.dateWindow })}
                            </UiText>
                            <UiText>
                                {formatDate(windowStartDate.toISOString(), language)}
                                {' - '}
                                {formatDate(windowEndDate.toISOString(), language)}
                            </UiText>
                        </UiStack>
                    </UiStack>

                    <UiStack
                        direction="column"
                        gap={SpacingToken.Micro}>
                        <Controller
                            name="customHour"
                            control={control}
                            render={({ field }) => (
                                <MigrationTimeWindowSelect
                                    value={field.value}
                                    onChange={field.onChange}
                                    localizedWindows={localizedWindows}
                                    className={classes.timeSelect}
                                />
                            )}
                        />
                        <UiText variant={FontVariantToken.fontSizeS}>
                            {translate({ id: 'CLIENT_RECOMMEND_TIME' })}
                        </UiText>
                    </UiStack>

                    <MigrationWhatsNextBox
                        whatsNextKey={i18n.whatsNext}
                        timeInfoKeys={i18n.timeInfoKeys}
                        className={classes.box}
                    />
                </>
            )}

            {/* ===== CUSTOM SCHEDULE MODE ===== */}
            {scheduleMode === 'flexible' && (
                <>
                    {/* Preferred dates disclaimer */}
                    <PortalAlertBar
                        status="info"
                        cancelable={false}>
                        <UiText variant={FontVariantToken.fontSizeS}>
                            {translate({ id: 'CLIENT_MIGRATION_PREFERRED_DATES_DISCLAIMER' })}
                        </UiText>
                    </PortalAlertBar>

                    <MigrationScheduleCalendar
                        selectedDates={customDates}
                        onDatesChange={(dates) => setValue('customDates', dates)}
                        minDate={calendarMinDate}
                        maxDate={calendarMaxDate}
                    />

                    {/* Timezone info */}
                    <MigrationTimezoneAlert tz={tz} />

                    {/* Time window dropdown */}
                    <UiStack
                        direction="column"
                        gap={SpacingToken.XS}>
                        <UiText variant={FontVariantToken.fontSizeMBold}>
                            {translate({ id: 'CLIENT_MIGRATION_DOWNTIME_WINDOW_LABEL' })}
                        </UiText>
                        <Controller
                            name="customHour"
                            control={control}
                            render={({ field }) => (
                                <MigrationTimeWindowSelect
                                    value={field.value}
                                    onChange={field.onChange}
                                    localizedWindows={localizedWindows}
                                    className={classes.timeSelect}
                                />
                            )}
                        />
                        <UiText variant={FontVariantToken.fontSizeS}>
                            {selectedTw.utcLabel}
                            {' '}
•
                            {translate({ id: 'CLIENT_RECOMMEND_TIME' })}
                        </UiText>
                    </UiStack>

                    <MigrationScheduleSummary selectedDates={customDates} />

                    {/* What happens next? */}
                    <MigrationWhatsNextBox
                        whatsNextKey={i18n.whatsNext}
                        timeInfoKeys={[
                            'CLIENT_MIGRATION_EMAIL_NOTIFICATION_INFO',
                            'CLIENT_MIGRATION_FLEXIBLE_INFO_2',
                            ...i18n.timeInfoKeys.slice(2).filter(k =>
                                k !== 'CLIENT_MIGRATION_EMAIL_NOTIFICATION_INFO' &&
                                k !== 'CLIENT_MIGRATION_FLEXIBLE_INFO_2'
                            ),
                        ]}
                        className={classes.box}
                    />
                </>
            )}
        </UiStack>
    );
};
